import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import { minutesOf, stamp, timeValue } from '@mds/shared/lib/dates';
import {
  FREE_TRIAL_BOOKING_END_DATE,
  FREE_TRIAL_BOOKING_START_DATE,
  FREE_TRIAL_EARLIEST_END_TIME,
  FREE_TRIAL_END_TIME,
  FREE_TRIAL_LATEST_START_TIME,
  FREE_TRIAL_START_TIME,
  isFreeTrialBookingDate,
} from '@mds/shared/lib/free-trial';
import type {
  AdminBooking,
  BookingLifecycleStatus,
  UpdateAdminBookingRequest,
} from '@mds/shared/types/booking';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import * as bookings from '@/bookings/bookings.api';
import PackagePicker from '@/components/booking/package-picker';
import PaymentFields from '@/components/booking/payment-fields';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, inputCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import { Cell, Row, Table } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import {
  blankPaymentDraft,
  bookingSpace,
  bookingWhen,
  cancelSchema,
  eligibilityPayload,
  paymentSchema,
  type PaymentDraft,
} from '@/domain/bookings';
import { formatCents } from '@/lib/money';
import { useValidation } from '@/lib/validation';
import * as rateCard from '@/rate-card/rate-card.api';

const PAGE_SIZE = 25;
const FREE_TRIAL_PACKAGE_ITEM_ID = 'free-trial';

const STATUS_LABELS: Record<BookingLifecycleStatus, string> = {
  payment_pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
};

const STATUS_TONES: Record<BookingLifecycleStatus, string> = {
  payment_pending: 'border-warn-edge bg-warn-wash text-warn-text',
  confirmed: 'border-mint/35 bg-mint-wash text-mint-deep',
  cancelled: 'border-line bg-ink/[0.05] text-ink-muted',
};

const PAYMENT_LABELS = {
  none: 'No payment required',
  cash: 'Cash',
  whish: 'Whish',
} as const;

const PAID_PAYMENT_OPTIONS = [
  { value: 'cash', label: PAYMENT_LABELS.cash },
  { value: 'whish', label: PAYMENT_LABELS.whish },
] as const;

interface EditRequestDraft {
  bookingDate: string;
  deskPackageItemId: string;
  deskId: string;
  startTime: string;
  endTime: string;
  paymentMethod: 'none' | 'cash' | 'whish';
}

const blankEditRequest: EditRequestDraft = {
  bookingDate: '',
  deskPackageItemId: '',
  deskId: '',
  startTime: '',
  endTime: '',
  paymentMethod: 'cash',
};

const validClockValue = (value: string) => /^(?:[01]\d|2[0-3]):(?:00|15|30|45)$/.test(value);

const REGION_LABELS = {
  akkar: 'Akkar',
  danniyeh: 'Danniyeh',
  minieh: 'Minieh',
  tripoli: 'Tripoli',
  other: 'Other',
} as const;
const WORK_MODE_LABELS = { onsite: 'On-site', remote: 'Remote', hybrid: 'Hybrid' } as const;

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 border-b border-hairline/70 pb-3 sm:grid-cols-[150px_1fr]">
      <span className="text-base text-ink-muted">{label}</span>
      <span className="text-base font-medium text-ink">{children}</span>
    </div>
  );
}

function requestError(error: unknown): string {
  return error instanceof ApiError ? error.message : 'That action did not go through. Try again.';
}

/** Public desk and meeting-room requests submitted before the visitor is handed to WhatsApp. */
export default function BookingRequestsPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('reservations.manage');
  const mayRecord = can('payments.record');

  const [page, setPage] = useState(1);
  const [hideCancelled, setHideCancelled] = useState(true);
  const [selected, setSelected] = useState<AdminBooking | null>(null);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<EditRequestDraft>(blankEditRequest);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [draft, setDraft] = useState<PaymentDraft>(blankPaymentDraft);
  const [reason, setReason] = useState('');
  const [sendCancellationEmail, setSendCancellationEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['booking-requests', { page, hideCancelled }],
    queryFn: () =>
      bookings.listBookingRequests({
        page,
        limit: PAGE_SIZE,
        excludeCancelled: hideCancelled,
        sort: 'createdAt',
        direction: 'desc',
      }),
  });
  const card = useQuery({
    queryKey: ['rate-card'],
    queryFn: rateCard.getRateCard,
    enabled: editing,
  });
  const rows = list.data?.data ?? [];
  const isDeskRequest = selected?.allocationType === 'desk';
  const editablePackages = useMemo(
    () =>
      (card.data?.deskPackages ?? []).filter(
        (item) =>
          item.package.isAvailable &&
          (item.id !== FREE_TRIAL_PACKAGE_ITEM_ID ||
            selected?.price.itemId === FREE_TRIAL_PACKAGE_ITEM_ID ||
            selected?.guestRequest?.details?.kind === 'trial'),
      ),
    [card.data, selected],
  );
  const editPackage = editablePackages.find((item) => item.id === editDraft.deskPackageItemId);
  const editTrial = isDeskRequest && editDraft.deskPackageItemId === FREE_TRIAL_PACKAGE_ITEM_ID;
  const editStartMinute = validClockValue(editDraft.startTime)
    ? minutesOf(editDraft.startTime)
    : undefined;
  const editEndMinute = validClockValue(editDraft.endTime)
    ? minutesOf(editDraft.endTime)
    : undefined;
  const editTrialWindowValid =
    !editTrial ||
    (isFreeTrialBookingDate(editDraft.bookingDate) &&
      editStartMinute !== undefined &&
      editEndMinute !== undefined &&
      editDraft.startTime >= FREE_TRIAL_START_TIME &&
      editDraft.startTime <= FREE_TRIAL_LATEST_START_TIME &&
      editDraft.endTime >= FREE_TRIAL_EARLIEST_END_TIME &&
      editDraft.endTime <= FREE_TRIAL_END_TIME &&
      editEndMinute > editStartMinute);
  const editAvailability = useQuery({
    queryKey: [
      'booking-request-edit-availability',
      selected?.resource.id,
      editDraft.bookingDate,
      isDeskRequest ? editDraft.deskPackageItemId : undefined,
      editTrial ? editStartMinute : undefined,
      editTrial ? editEndMinute : undefined,
    ],
    queryFn: () =>
      bookings.getAvailability(
        selected!.resource.id,
        editDraft.bookingDate,
        isDeskRequest ? editDraft.deskPackageItemId : undefined,
        editTrial ? editStartMinute : undefined,
        editTrial ? editEndMinute : undefined,
      ),
    enabled:
      editing &&
      Boolean(selected && isDeskRequest && editDraft.bookingDate && editPackage) &&
      editTrialWindowValid,
  });
  const availableDesks = useMemo(
    () => editAvailability.data?.desks.filter((desk) => desk.isActive && desk.available) ?? [],
    [editAvailability.data],
  );
  const paidRequest = Boolean(selected && selected.payment.totalAmountMinor > 0);
  const paymentCheck = useValidation(
    useMemo(() => paymentSchema(paidRequest ? selected : null), [paidRequest, selected]),
    draft,
  );
  const cancellation = useMemo(() => ({ reason }), [reason]);
  const cancelCheck = useValidation(cancelSchema, cancellation);

  const refresh = async (next?: AdminBooking) => {
    if (next) setSelected(next);
    await queryClient.invalidateQueries({ queryKey: ['booking-requests'] });
    await queryClient.invalidateQueries({ queryKey: ['bookings'] });
    await queryClient.invalidateQueries({ queryKey: ['availability'] });
    await queryClient.invalidateQueries({ queryKey: ['booking-request-edit-availability'] });
    await queryClient.invalidateQueries({ queryKey: ['payments'] });
  };

  const beginEdit = () => {
    if (!selected) return;
    const requested = selected.guestRequest?.requestedPaymentMethod;
    setEditDraft({
      bookingDate: selected.bookingDate,
      deskPackageItemId: selected.allocationType === 'desk' ? selected.price.itemId : '',
      deskId: selected.desk?.id ?? '',
      startTime: selected.startMinute === null ? '' : timeValue(selected.startMinute),
      endTime: selected.endMinute === null ? '' : timeValue(selected.endMinute),
      paymentMethod:
        requested === 'none' || requested === 'cash' || requested === 'whish'
          ? requested
          : selected.payment.totalAmountMinor === 0
            ? 'none'
            : 'cash',
    });
    setProblem(null);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!selected) return;
    if (!editDraft.bookingDate) {
      setProblem('Choose a booking date.');
      return;
    }

    let body: UpdateAdminBookingRequest;
    if (selected.allocationType === 'desk') {
      if (!editPackage) {
        setProblem('Choose an available desk package.');
        return;
      }
      if (editTrial && !editTrialWindowValid) {
        setProblem('Choose a valid free-trial date and time window.');
        return;
      }
      if (editAvailability.isPending) {
        setProblem('Wait for desk availability to finish loading.');
        return;
      }
      if (editAvailability.isError) {
        setProblem(requestError(editAvailability.error));
        return;
      }
      if (!availableDesks.some((desk) => desk.id === editDraft.deskId)) {
        setProblem('Choose an available desk for the selected date and package.');
        return;
      }
      if (editTrial) {
        body = {
          bookingDate: editDraft.bookingDate,
          deskId: editDraft.deskId,
          deskPackageItemId: FREE_TRIAL_PACKAGE_ITEM_ID,
          startMinute: editStartMinute!,
          endMinute: editEndMinute!,
          requestedPaymentMethod: 'none',
        };
      } else {
        if (editDraft.paymentMethod !== 'cash' && editDraft.paymentMethod !== 'whish') {
          setProblem('Choose how the customer plans to pay.');
          return;
        }
        body = {
          bookingDate: editDraft.bookingDate,
          deskId: editDraft.deskId,
          deskPackageItemId: editDraft.deskPackageItemId,
          requestedPaymentMethod: editDraft.paymentMethod,
        };
      }
    } else {
      if (
        !validClockValue(editDraft.startTime) ||
        !validClockValue(editDraft.endTime) ||
        editStartMinute === undefined ||
        editEndMinute === undefined ||
        editStartMinute % 60 !== 0 ||
        editEndMinute % 60 !== 0 ||
        editEndMinute <= editStartMinute
      ) {
        setProblem('Choose a whole-hour start and end time.');
        return;
      }
      if (editDraft.paymentMethod !== 'cash' && editDraft.paymentMethod !== 'whish') {
        setProblem('Choose how the customer plans to pay.');
        return;
      }
      body = {
        bookingDate: editDraft.bookingDate,
        startMinute: editStartMinute,
        endMinute: editEndMinute,
        requestedPaymentMethod: editDraft.paymentMethod,
      };
    }

    setBusy(true);
    setProblem(null);
    try {
      const next = await bookings.updateBookingRequest(selected.id, body);
      await refresh(next);
      setEditing(false);
    } catch (error) {
      setProblem(requestError(error));
      await queryClient.invalidateQueries({ queryKey: ['booking-request-edit-availability'] });
    } finally {
      setBusy(false);
    }
  };

  const beginConfirm = () => {
    if (!selected) return;
    const requested = selected.guestRequest?.requestedPaymentMethod;
    setDraft({
      ...blankPaymentDraft,
      choice: selected.payment.totalAmountMinor > 0 ? 'deposit' : 'full',
      method: requested === 'cash' || requested === 'whish' ? requested : 'cash',
    });
    paymentCheck.reset();
    setProblem(null);
    setConfirming(true);
  };

  const confirm = async () => {
    if (!selected) return;
    if (paidRequest && !paymentCheck.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      const next = await bookings.confirmBookingRequest(
        selected.id,
        paidRequest
          ? {
              settlement: draft.choice,
              method: draft.method,
              ...(draft.method === 'other' ? { methodDetail: draft.methodDetail.trim() } : {}),
              ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
              ...eligibilityPayload(selected, draft),
            }
          : {},
      );
      await refresh(next);
      setConfirming(false);
    } catch (error) {
      setProblem(requestError(error));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!selected || !cancelCheck.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      const next = await bookings.cancelBooking(selected.id, {
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        sendCancellationEmail,
      });
      await refresh(next);
      setCancelling(false);
    } catch (error) {
      setProblem(requestError(error));
    } finally {
      setBusy(false);
    }
  };

  const details = selected?.guestRequest?.details ?? null;
  const requestedMethod = selected?.guestRequest?.requestedPaymentMethod ?? null;

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={list.data ? `${list.data.pagination.total} booking requests` : 'Booking requests'}
        action={
          <Toolbar>
            <label
              className={
                controlCls + ' flex cursor-pointer items-center gap-2 font-medium whitespace-nowrap'
              }
            >
              <input
                type="checkbox"
                className="h-5 w-5 shrink-0 accent-brand-edge"
                checked={hideCancelled}
                onChange={(event) => {
                  setHideCancelled(event.target.checked);
                  setPage(1);
                }}
              />
              Hide cancelled
            </label>
          </Toolbar>
        }
      >
        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading booking requests…</p>
        ) : list.isError ? (
          <EmptyState title="Could not load booking requests" hint={requestError(list.error)} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={hideCancelled ? 'No pending booking requests' : 'No booking requests yet'}
            hint={
              hideCancelled
                ? 'New requests appear here when a visitor clicks Book on WhatsApp.'
                : 'There are no pending or cancelled booking requests.'
            }
          />
        ) : (
          <Table
            head={[
              'Reference',
              'Customer',
              'Booking',
              'Package',
              'Payment way',
              'Status',
              'Received',
            ]}
          >
            {rows.map((booking) => {
              const method = booking.guestRequest?.requestedPaymentMethod;
              return (
                <Row
                  key={booking.id}
                  onClick={() => {
                    setProblem(null);
                    setSelected(booking);
                  }}
                >
                  <Cell className="font-mono text-md">{booking.reference}</Cell>
                  <Cell>
                    <div className="flex flex-col">
                      <span className="font-semibold">{booking.customer.name}</span>
                      <span className="text-md text-ink-muted">{booking.customer.phone}</span>
                    </div>
                  </Cell>
                  <Cell>
                    <div className="flex flex-col">
                      <span>{bookingSpace(booking)}</span>
                      <span className="font-mono text-md text-ink-muted">
                        {bookingWhen(booking)}
                      </span>
                    </div>
                  </Cell>
                  <Cell>{booking.price.itemName}</Cell>
                  <Cell>{method ? PAYMENT_LABELS[method] : 'Not captured'}</Cell>
                  <Cell>
                    <Chip className={'border ' + STATUS_TONES[booking.lifecycleStatus]}>
                      {STATUS_LABELS[booking.lifecycleStatus]}
                    </Chip>
                  </Cell>
                  <Cell className="font-mono text-md">{stamp(booking.bookedAt)}</Cell>
                </Row>
              );
            })}
          </Table>
        )}
        <Pager pagination={list.data?.pagination} onPage={setPage} />
      </Card>

      <Modal
        open={selected !== null && !editing && !confirming && !cancelling}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.reference} · Booking request` : 'Booking request'}
        footer={
          selected ? (
            <>
              {mayManage && selected.lifecycleStatus === 'payment_pending' && (
                <Button variant="quiet" onClick={beginEdit}>
                  Edit request
                </Button>
              )}
              {mayManage && selected.lifecycleStatus === 'payment_pending' && (
                <Button
                  variant="mint"
                  disabled={selected.payment.totalAmountMinor > 0 && !mayRecord}
                  onClick={beginConfirm}
                >
                  {selected.payment.totalAmountMinor > 0
                    ? 'Confirm and record payment'
                    : 'Confirm trial'}
                </Button>
              )}
              {mayManage && selected.lifecycleStatus === 'payment_pending' && (
                <Button
                  variant="danger"
                  onClick={() => {
                    setReason('');
                    setSendCancellationEmail(false);
                    setProblem(null);
                    cancelCheck.reset();
                    setCancelling(true);
                  }}
                >
                  Cancel request
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {selected && (
          <>
            {problem && (
              <p className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text">
                {problem}
              </p>
            )}
            <div className="flex flex-col gap-3">
              <Line label="Status">{STATUS_LABELS[selected.lifecycleStatus]}</Line>
              <Line label="Customer">{selected.customer.name}</Line>
              <Line label="Phone">{selected.customer.phone}</Line>
              <Line label="Email">{selected.customer.email}</Line>
              <Line label="Booking">{bookingSpace(selected)}</Line>
              <Line label="When">{bookingWhen(selected)}</Line>
              <Line label="Package">{selected.price.itemName}</Line>
              <Line label="Price">{formatCents(selected.price.totalAmountMinor)}</Line>
              <Line label="Payment way">
                {requestedMethod ? PAYMENT_LABELS[requestedMethod] : 'Not captured'}
              </Line>
              {details?.kind === 'trial' && (
                <>
                  <Line label="Lives in">{REGION_LABELS[details.region]}</Line>
                  <Line label="Occupation">
                    {details.occupation === 'student' ? 'Student' : 'Employee'}
                  </Line>
                  {details.occupation === 'student' ? (
                    <Line label="Major">{details.major}</Line>
                  ) : (
                    <>
                      <Line label="Workplace type">{WORK_MODE_LABELS[details.workMode]}</Line>
                      <Line label="Field of work">{details.workField}</Line>
                    </>
                  )}
                </>
              )}
              <Line label="Terms accepted">
                {details ? stamp(details.policyAcceptedAt) : 'Legacy request'}
              </Line>
            </div>
            {selected.payment.totalAmountMinor > 0 &&
              !mayRecord &&
              selected.lifecycleStatus === 'payment_pending' && (
                <p className="m-0 text-base text-warn-text">
                  Confirming a paid request requires permission to record payments.
                </p>
              )}
          </>
        )}
      </Modal>

      <Modal
        open={editing && selected !== null}
        onClose={() => !busy && setEditing(false)}
        title={selected ? `${selected.reference} · Edit request` : 'Edit booking request'}
        footer={
          <>
            <Button variant="mint" disabled={busy} onClick={() => void saveEdit()}>
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setEditing(false)}>
              Back
            </Button>
          </>
        }
      >
        {problem && (
          <p className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text">
            {problem}
          </p>
        )}
        {selected && (
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Date" required>
              <input
                type="date"
                className={inputCls}
                value={editDraft.bookingDate}
                min={editTrial ? FREE_TRIAL_BOOKING_START_DATE : undefined}
                max={editTrial ? FREE_TRIAL_BOOKING_END_DATE : undefined}
                onChange={(event) => {
                  setEditDraft((current) => ({
                    ...current,
                    bookingDate: event.target.value,
                  }));
                  setProblem(null);
                }}
              />
            </Field>

            {selected.allocationType === 'desk' &&
              (card.isPending ? (
                <p className="m-0 self-end text-base text-ink-muted">Loading packages…</p>
              ) : card.isError ? (
                <p className="m-0 self-end text-base text-danger-text">
                  {requestError(card.error)}
                </p>
              ) : (
                <PackagePicker
                  packages={editablePackages}
                  value={editDraft.deskPackageItemId}
                  onChange={(next) => {
                    const trial = next === FREE_TRIAL_PACKAGE_ITEM_ID;
                    setEditDraft((current) => ({
                      ...current,
                      deskPackageItemId: next,
                      bookingDate:
                        trial && !isFreeTrialBookingDate(current.bookingDate)
                          ? FREE_TRIAL_BOOKING_START_DATE
                          : current.bookingDate,
                      startTime: trial ? current.startTime : '',
                      endTime: trial ? current.endTime : '',
                      paymentMethod: trial
                        ? 'none'
                        : current.paymentMethod === 'none'
                          ? 'cash'
                          : current.paymentMethod,
                    }));
                    setProblem(null);
                  }}
                  variant="select"
                />
              ))}

            {selected.allocationType === 'desk' && editTrial && (
              <>
                <Field label="Trial starts" required>
                  <input
                    type="time"
                    step={900}
                    min={FREE_TRIAL_START_TIME}
                    max={FREE_TRIAL_LATEST_START_TIME}
                    className={inputCls}
                    value={editDraft.startTime}
                    onChange={(event) => {
                      setEditDraft((current) => ({
                        ...current,
                        startTime: event.target.value,
                      }));
                      setProblem(null);
                    }}
                  />
                </Field>
                <Field label="Trial ends" required>
                  <input
                    type="time"
                    step={900}
                    min={FREE_TRIAL_EARLIEST_END_TIME}
                    max={FREE_TRIAL_END_TIME}
                    className={inputCls}
                    value={editDraft.endTime}
                    onChange={(event) => {
                      setEditDraft((current) => ({
                        ...current,
                        endTime: event.target.value,
                      }));
                      setProblem(null);
                    }}
                  />
                </Field>
              </>
            )}

            {selected.allocationType === 'room' && (
              <>
                <Field label="Starts" required>
                  <input
                    type="time"
                    step={3600}
                    className={inputCls}
                    value={editDraft.startTime}
                    onChange={(event) => {
                      setEditDraft((current) => ({
                        ...current,
                        startTime: event.target.value,
                      }));
                      setProblem(null);
                    }}
                  />
                </Field>
                <Field label="Ends" required>
                  <input
                    type="time"
                    step={3600}
                    className={inputCls}
                    value={editDraft.endTime}
                    onChange={(event) => {
                      setEditDraft((current) => ({
                        ...current,
                        endTime: event.target.value,
                      }));
                      setProblem(null);
                    }}
                  />
                </Field>
              </>
            )}

            {selected.allocationType === 'desk' && (
              <Field
                label="Desk"
                required
                hint={
                  editAvailability.isPending
                    ? 'Checking the selected package’s full booking period…'
                    : 'Only desks available for the complete booking period are listed.'
                }
              >
                <Select
                  variant="field"
                  value={editDraft.deskId}
                  disabled={!editAvailability.data || availableDesks.length === 0}
                  placeholder={
                    editAvailability.isPending
                      ? 'Checking availability…'
                      : availableDesks.length === 0
                        ? 'No desks available'
                        : 'Choose a desk'
                  }
                  options={availableDesks.map((desk) => ({
                    value: desk.id,
                    label: desk.deskId,
                  }))}
                  onChange={(deskId) => {
                    setEditDraft((current) => ({ ...current, deskId }));
                    setProblem(null);
                  }}
                  aria-label="Desk"
                />
              </Field>
            )}

            {editTrial ? (
              <Line label="Payment way">{PAYMENT_LABELS.none}</Line>
            ) : (
              <Field label="Payment way" required>
                <Select
                  variant="field"
                  value={editDraft.paymentMethod}
                  options={PAID_PAYMENT_OPTIONS}
                  onChange={(paymentMethod) => {
                    setEditDraft((current) => ({
                      ...current,
                      paymentMethod: paymentMethod as 'cash' | 'whish',
                    }));
                    setProblem(null);
                  }}
                  aria-label="Payment way"
                />
              </Field>
            )}

            <div className="sm:col-span-2">
              <p className="m-0 text-base text-ink-muted">
                {editPackage
                  ? `The updated request will use ${editPackage.name} at ${formatCents(editPackage.amountMinor)} per ${editPackage.unit}.`
                  : 'Availability and price are recalculated when the request is saved.'}
              </p>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={confirming && selected !== null}
        onClose={() => !busy && setConfirming(false)}
        title={paidRequest ? 'Confirm request and record payment' : 'Confirm free trial'}
        footer={
          <>
            <Button variant="mint" disabled={busy} onClick={() => void confirm()}>
              {busy ? 'Confirming…' : 'Confirm booking request'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setConfirming(false)}>
              Back
            </Button>
          </>
        }
      >
        {problem && (
          <p className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text">
            {problem}
          </p>
        )}
        {selected && paidRequest ? (
          <PaymentFields
            booking={selected}
            draft={draft}
            onChange={setDraft}
            field={paymentCheck.field}
          />
        ) : (
          <p className="m-0 text-base">
            This checks availability again and reserves the selected trial desk and time. No payment
            record is created.
          </p>
        )}
      </Modal>

      <Modal
        open={cancelling && selected !== null}
        onClose={() => !busy && setCancelling(false)}
        title="Cancel booking request"
        footer={
          <>
            <Button variant="danger" disabled={busy} onClick={() => void cancel()}>
              {busy ? 'Cancelling…' : 'Cancel request'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setCancelling(false)}>
              Back
            </Button>
          </>
        }
      >
        {problem && (
          <p className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text">
            {problem}
          </p>
        )}
        <label className="flex items-center gap-2.5 text-base">
          <input
            type="checkbox"
            className="h-6 w-6 accent-brand-edge"
            checked={sendCancellationEmail}
            disabled={busy}
            onChange={(event) => setSendCancellationEmail(event.target.checked)}
          />
          Send a cancellation email to the customer
        </label>
        <Field
          label="Reason"
          hint={
            sendCancellationEmail
              ? 'Optional. Included in the cancellation email.'
              : 'Optional. Saved internally and not emailed.'
          }
          {...cancelCheck.field('reason')}
        >
          <textarea
            rows={4}
            className={inputCls}
            value={reason}
            maxLength={500}
            placeholder="The requested desk is no longer available."
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}
